import { v4 as uuidv4 } from 'uuid';
import { supabase, supabaseAdmin } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';
import { useStorage } from '@/composables/useStorage';
import { StorageBucket } from '@/types/storage';
import { TemplateStorageError, TemplateValidationError, TemplateNotFoundError, StorageServiceError } from '../models/errors';
import { validateExcelTemplate, extractTemplateMetadata } from '../utils/templateValidator';
/**
 * Service for template storage operations
 */
export class TemplateStorageService {
    constructor() {
        Object.defineProperty(this, "storage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: useStorage()
        });
    }
    /**
     * Initialize the storage service by ensuring the Templates bucket exists
     */
    async initialize() {
        try {
            // Assume the Templates bucket always exists
            logger.info('Assuming Templates bucket exists, continuing');
        }
        catch (error) {
            logger.error('Error initializing template storage service', { error });
            throw new Error(`Failed to initialize template storage service: ${error instanceof Error ? error.message : String(error)}`);
        }
    }
    /**
     * Upload a template file to storage and create metadata
     * @param file The template file to upload
     * @param metadata Additional metadata for the template
     * @returns The created template record
     */
    async uploadTemplate(file, metadata) {
        try {
            // Ensure the Templates bucket exists before uploading
            await this.initialize();
            // Validate the template
            const validationResult = await validateExcelTemplate(file);
            if (!validationResult.isValid) {
                logger.warn('Template validation failed', LogCategory.STORAGE, {
                    filename: file.name,
                    validationErrors: validationResult.errors
                });
                throw new TemplateValidationError('Template validation failed', validationResult);
            }
            await extractTemplateMetadata(file);
            // Generate a unique filename
            const fileName = `${uuidv4()}-${file.name}`;
            // Upload to Supabase storage
            try {
                logger.info('Attempting to upload file to Supabase storage', LogCategory.STORAGE, {
                    bucket: StorageBucket.TEMPLATES,
                    fileName,
                    fileSize: file.size,
                    fileType: file.type
                });
                const uploadResult = await this.storage.uploadFile(StorageBucket.TEMPLATES, fileName, file);
                if (!uploadResult || !uploadResult.path) {
                    throw new Error('Upload failed: No path returned from storage service');
                }
                const path = uploadResult.path;
                // Create metadata entry in files table with required fields
                // The 'url' field is required and cannot be null
                // Extract file extension to store as type
                const fileExtension = file.name.split('.').pop() || '';
                // Calculate file size in kilobytes (rounded up)
                const fileSizeKB = Math.ceil(file.size / 1024);
                const templateData = {
                    name: metadata.name,
                    url: path || fileName,
                    type: fileExtension.toLowerCase(), // Store file type (e.g., "xlsx")
                    size: fileSizeKB // Store file size in KB (rounded up)
                };
                let data = null;
                let error = null;
                // Use the admin client to bypass RLS policies if available
                const client = supabaseAdmin || supabase;
                const { data: resultData, error: resultError } = await client
                    .from('files')
                    .insert(templateData)
                    .select()
                    .single();
                data = resultData;
                error = resultError;
                if (error) {
                    // If metadata creation fails, delete the uploaded file
                    await this.storage.deleteFile(StorageBucket.TEMPLATES, path || fileName);
                    // Log detailed error information
                    logger.error('Failed to insert template metadata into database', LogCategory.STORAGE, {
                        error,
                        templateData,
                        errorCode: error.code,
                        errorMessage: error.message,
                        errorDetails: error.details
                    });
                    // Provide more specific error messages based on error code
                    if (error.code === '23502') { // not_null_violation
                        throw new Error(`Database error: Missing required field - ${error.details || error.message}`);
                    }
                    else if (error.code === '23505') { // unique_violation
                        throw new Error(`Database error: Duplicate entry - ${error.details || error.message}`);
                    }
                    else if (error.code === '22P02') { // invalid_text_representation (often for UUID format issues)
                        throw new Error(`Database error: Invalid UUID format - ${error.details || error.message}`);
                    }
                    else if (error.code === '23503') { // foreign_key_violation
                        // Handle foreign key constraint violation
                        if (error.message && error.message.includes('templates_user_id_fkey')) {
                            throw new Error(`Database error: User ID does not exist - ${error.details || error.message}. Please use a valid user ID that exists in the users table.`);
                        }
                        else {
                            throw new Error(`Database error: Foreign key constraint violation - ${error.details || error.message}`);
                        }
                    }
                    else {
                        throw error;
                    }
                }
                // Ensure data is not null before using it
                if (!data) {
                    throw new Error('Template data is null after successful upload');
                }
                logger.info('Template uploaded successfully', LogCategory.STORAGE, {
                    templateId: data.id,
                    filename: file.name,
                    storagePath: path || fileName,
                    fileSize: file.size,
                    fileType: file.type
                });
                return data;
            }
            catch (error) {
                logger.error('Template upload failed', LogCategory.STORAGE, {
                    filename: file.name,
                    error: error instanceof Error ? error.message : String(error),
                    errorObject: error
                });
                // Check for specific error types
                if (typeof error === 'object' && error !== null) {
                    const errorObj = error;
                    // Check for bucket not found error
                    if (errorObj.statusCode === '404' && errorObj.message === 'Bucket not found') {
                        throw new StorageServiceError(`Bucket "${StorageBucket.TEMPLATES}" not found. Please create it in the Supabase dashboard.`, StorageBucket.TEMPLATES, fileName, error instanceof Error ? error : new Error(String(error)));
                    }
                    // Check for permission denied error
                    if (errorObj.statusCode === '403' && errorObj.message && errorObj.message.includes('row-level security policy')) {
                        throw new StorageServiceError('Permission denied due to RLS policy. Make sure you have the correct permissions.', StorageBucket.TEMPLATES, fileName, error instanceof Error ? error : new Error(String(error)));
                    }
                }
                // Generic error
                throw new StorageServiceError('Failed to upload template to storage', StorageBucket.TEMPLATES, fileName, error instanceof Error ? error : new Error(String(error)));
            }
        }
        catch (error) {
            // Handle and log the error
            if (error instanceof TemplateValidationError ||
                error instanceof StorageServiceError) {
                throw error; // Re-throw known errors
            }
            // Log and wrap unknown errors
            logger.error('Unexpected error during template upload', LogCategory.STORAGE, {
                filename: file.name,
                error
            });
            throw new TemplateStorageError('Unexpected error during template upload', error instanceof Error ? error : new Error(String(error)));
        }
    }
    /**
     * Get templates
     * @param options Query options (limit, offset, sorting)
     * @returns Array of templates
     */
    async getTemplates(options) {
        try {
            // Now that the query is working, let's return the actual data
            const query = supabase
                .from('files')
                .select('*');
            const { data, error } = await query;
            if (error) {
                logger.error('Database query error', LogCategory.STORAGE, {
                    error,
                    errorMessage: error.message,
                    errorCode: error.code,
                    errorDetails: error.details
                });
                return [];
            }
            logger.info('Successfully retrieved templates', LogCategory.STORAGE, {
                dataCount: data?.length || 0
            });
            return data || [];
        }
        catch (error) {
            logger.error('Error fetching templates', LogCategory.STORAGE, {
                options,
                error
            });
            return [];
        }
    }
    /**
     * Get the latest template
     * @returns The latest template or null if none found
     */
    async getLatestTemplate() {
        try {
            // Now that the query is working, let's return the actual data
            const query = supabase
                .from('files')
                .select('*')
                .limit(1)
                .single();
            const { data, error } = await query;
            if (error) {
                if (error.code === 'PGRST116') {
                    // No rows returned
                    return null;
                }
                logger.error('Database query error for latest template', LogCategory.STORAGE, {
                    error,
                    errorMessage: error.message,
                    errorCode: error.code,
                    errorDetails: error.details
                });
                return null;
            }
            logger.info('Successfully retrieved latest template', LogCategory.STORAGE, {
                hasData: !!data
            });
            return data;
        }
        catch (error) {
            logger.error('Error fetching latest template', LogCategory.STORAGE, {
                error
            });
            return null;
        }
    }
    /**
     * Get templates by timestamp range
     * @param startDate Start date (ISO string)
     * @param endDate End date (ISO string)
     * @param options Query options
     * @returns Array of templates
     */
    async getTemplatesByTimestamp(startDate, endDate, options) {
        try {
            // Since the created_at column doesn't exist, we can't filter by timestamp
            // Return an empty array for now
            logger.info('Returning empty array for templates by timestamp', LogCategory.STORAGE, {
                startDate,
                endDate,
                options
            });
            return [];
        }
        catch (error) {
            logger.error('Error in getTemplatesByTimestamp', LogCategory.STORAGE, {
                startDate,
                endDate,
                options,
                error
            });
            return [];
        }
    }
    /**
     * Get a template by ID
     * @param templateId The template ID
     * @returns The template or null if not found
     */
    async getTemplateById(templateId) {
        try {
            // Now that the query is working, let's return the actual data
            const query = supabase
                .from('files')
                .select('*')
                .eq('id', templateId)
                .single();
            const { data, error } = await query;
            if (error) {
                if (error.code === 'PGRST116') {
                    // No rows returned
                    return null;
                }
                logger.error('Database query error for template by ID', LogCategory.STORAGE, {
                    templateId,
                    error,
                    errorMessage: error.message,
                    errorCode: error.code,
                    errorDetails: error.details
                });
                return null;
            }
            logger.info('Successfully retrieved template by ID', LogCategory.STORAGE, {
                templateId,
                hasData: !!data
            });
            return data;
        }
        catch (error) {
            logger.error('Error fetching template by ID', LogCategory.STORAGE, {
                templateId,
                error
            });
            return null;
        }
    }
    /**
     * Download a template by ID
     * @param templateId The template ID
     * @returns The template file as a Blob
     */
    async downloadTemplate(templateId) {
        try {
            // Get template record
            const template = await this.getTemplateById(templateId);
            if (!template) {
                throw new TemplateNotFoundError(`Template with ID ${templateId} not found`);
            }
            // Update last_accessed in the database
            const updates = {
                last_accessed: new Date().toISOString()
            };
            await supabase
                .from('files')
                .update(updates)
                .eq('id', templateId);
            // Download file from storage
            const fileBlob = await this.storage.downloadFile(StorageBucket.TEMPLATES, template.storage_path || '');
            if (!fileBlob) {
                throw new StorageServiceError('Failed to download template file', StorageBucket.TEMPLATES, template.storage_path || '');
            }
            return fileBlob;
        }
        catch (error) {
            logger.error('Error downloading template', LogCategory.STORAGE, {
                templateId,
                error
            });
            if (error instanceof TemplateNotFoundError || error instanceof StorageServiceError) {
                throw error;
            }
            throw new TemplateStorageError(`Failed to download template with ID ${templateId}`, error instanceof Error ? error : new Error(String(error)));
        }
    }
    /**
     * Get a template with its file data
     * @param templateId The template ID
     * @returns The template with file data
     */
    async getTemplateWithFile(templateId) {
        try {
            const template = await this.getTemplateById(templateId);
            if (!template) {
                throw new TemplateNotFoundError(`Template with ID ${templateId} not found`);
            }
            const fileBlob = await this.downloadTemplate(templateId);
            return {
                ...template,
                file: fileBlob
            };
        }
        catch (error) {
            logger.error('Error fetching template with file', LogCategory.STORAGE, {
                templateId,
                error
            });
            if (error instanceof TemplateNotFoundError || error instanceof StorageServiceError) {
                throw error;
            }
            throw new TemplateStorageError(`Failed to fetch template with file for ID ${templateId}`, error instanceof Error ? error : new Error(String(error)));
        }
    }
    /**
     * Update a template's metadata
     * @param templateId The template ID
     * @param updates The updates to apply
     * @returns The updated template
     */
    async updateTemplate(templateId, updates) {
        try {
            const { data, error } = await supabase
                .from('files')
                .update(updates)
                .eq('id', templateId)
                .select()
                .single();
            if (error) {
                throw error;
            }
            return data;
        }
        catch (error) {
            logger.error('Error updating template', LogCategory.STORAGE, {
                templateId,
                updates,
                error
            });
            throw new TemplateStorageError(`Failed to update template with ID ${templateId}`, error instanceof Error ? error : new Error(String(error)));
        }
    }
    /**
     * Delete a template and its file
     * @param templateId The template ID
     * @returns True if successful
     */
    async deleteTemplate(templateId) {
        try {
            // Get template record
            const template = await this.getTemplateById(templateId);
            if (!template) {
                throw new TemplateNotFoundError(`Template with ID ${templateId} not found`);
            }
            // Delete file from storage
            if (template.storage_path) {
                await this.storage.deleteFile(StorageBucket.TEMPLATES, template.storage_path);
            }
            // Delete metadata from database
            const { error } = await supabase
                .from('files')
                .delete()
                .eq('id', templateId);
            if (error) {
                throw error;
            }
            return true;
        }
        catch (error) {
            logger.error('Error deleting template', LogCategory.STORAGE, {
                templateId,
                error
            });
            if (error instanceof TemplateNotFoundError) {
                throw error;
            }
            throw new TemplateStorageError(`Failed to delete template with ID ${templateId}`, error instanceof Error ? error : new Error(String(error)));
        }
    }
}
